<template>
  <section>
    <v-progress-linear
      v-if="isLoadingReservation"
      indeterminate
    />

    <AppPageTitleBar
      :title="route.meta.title"
      :back-to="calendarRoute"
      back-label="Natrag"
    />

    <v-form
      v-if="!isEditMode || reservationToEdit"
      ref="reservationFormRef"
      class="form form--with-sticky-actions page-stack grid-stack grid-gap-page-section reservation-form"
      @submit.prevent="saveReservationAsync"
    >
      <section class="page-panel">
        <header class="page-panel__header reservation-form__section-heading">
          <h3 class="panel-title">Podaci o rezervaciji</h3>
        </header>

        <div class="page-panel__content grid-stack grid-row-gap-18">
          <AppSelect
            v-model="reservationFormValues.vehicleId"
            class="reservation-form__field"
            :items="vehicleOptions"
            :disabled="isLoadingVehicles"
            :placeholder="isLoadingVehicles ? 'Učitavanje vozila...' : 'Odaberite vozilo'"
            label="Vozilo"
            no-data-text="Nema pronađenih vozila."
            searchable
            required
          >
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <template #title>
                  {{ item.raw.brand }} {{ item.raw.name }}
                  <span class="reservation-form__vehicle-plate">{{ item.raw.plate }}</span>
                </template>
              </v-list-item>
            </template>
            <template #selection="{ item }">
              {{ item.raw.brand }} {{ item.raw.name }}
              <span class="reservation-form__vehicle-plate">{{ item.raw.plate }}</span>
            </template>
          </AppSelect>

          <div class="grid grid-column-gap-16">
            <div class="col-12 col-sm-6">
              <AppDateField
                v-model="reservationFormValues.startDate"
                label="Od"
                required
              />
            </div>

            <div class="col-12 col-sm-6">
              <AppDateField
                v-model="reservationFormValues.endDate"
                label="Do"
                required
              />
            </div>
          </div>
        </div>

        <header class="page-panel__header reservation-form__section-heading reservation-form__driver-heading">
          <h3 class="panel-title">Podaci o vozaču</h3>
        </header>

        <div class="page-panel__content grid-stack grid-row-gap-18">
          <AppTextField
            v-model="reservationFormValues.company"
            class="reservation-form__field"
            label="Tvrtka"
            placeholder="Unesite naziv"
            :rules="maxLengthRules"
          />

          <AppTextField
            v-model="reservationFormValues.fullName"
            class="reservation-form__field"
            label="Ime i prezime"
            placeholder="Unesite ime i prezime"
            :rules="maxLengthRules"
          />

          <AppTextField
            v-model="reservationFormValues.contact"
            class="reservation-form__field"
            label="Email / Telefon"
            placeholder="npr. email@primjer.hr"
            :rules="maxLengthRules"
          />
        </div>
      </section>

      <div class="submit-bar">
        <ToolbarActionButton
          text="Prihvati rezervaciju"
          type="submit"
          icon=""
          :disabled="isLoadingReservation || isLoadingVehicles || isSavingReservation"
          :loading="isSavingReservation"
        />
      </div>
    </v-form>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppDateField from '../../components/AppDateField.vue'
import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import AppSelect from '../../components/AppSelect.vue'
import AppTextField from '../../components/AppTextField.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { getEventBrandName } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
const isEditMode = computed(() => Boolean(route.params.reservationId))
const reservationFormRef = ref(null)
const reservationToEdit = ref(null)
const vehicleOptions = ref([])
const isLoadingReservation = ref(isEditMode.value)
const isLoadingVehicles = ref(true)
const isSavingReservation = ref(false)
const maxLengthRules = [
  (value) => String(value ?? '').trim().length <= 255 || 'Najviše 255 znakova',
]
const calendarRoute = computed(() => ({
  name: 'fleet-calendar',
  query: route.query.month ? { month: route.query.month } : {},
}))
const reservationFormValues = reactive({
  vehicleId: typeof route.query.vehicleId === 'string' ? route.query.vehicleId : null,
  startDate: typeof route.query.startDate === 'string' ? route.query.startDate : '',
  endDate: '',
  company: '',
  fullName: '',
  contact: '',
})

onMounted(loadReservationFormAsync)

async function loadReservationFormAsync () {
  if (isEditMode.value) {
    await getReservationAsync()

    if (!reservationToEdit.value) {
      return
    }
  }

  await getVehiclesAsync()
}

async function getReservationAsync () {
  try {
    const reservation = await apiClient.getAsync(`/api/drivepark/reservations/${route.params.reservationId}`)

    reservationToEdit.value = reservation
    Object.assign(reservationFormValues, {
      vehicleId: reservation.vehicleId,
      startDate: reservation.startDate,
      endDate: reservation.endDate,
      company: reservation.company ?? '',
      fullName: reservation.fullName ?? '',
      contact: reservation.contact ?? '',
    })
  } catch (error) {
    console.error('Failed to load the drive park reservation for editing.', error)
    showRequestErrorToast(error, 'Rezervaciju nije bilo moguće učitati.')
  } finally {
    isLoadingReservation.value = false
  }
}

async function getVehiclesAsync () {
  try {
    const vehicles = []
    let page = 1
    let totalResults = 0

    do {
      const response = await apiClient.getAsync('/api/vehicles', {
        queryParams: { page, perPage: 100 },
      })

      vehicles.push(...response.results)
      totalResults = response.pagination.totalResults
      page += 1
    } while (vehicles.length < totalResults)

    vehicleOptions.value = vehicles.map((vehicle) => {
      const brand = getEventBrandName(vehicle.brandCode)

      return {
        title: `${brand} ${vehicle.name} ${vehicle.plate}`,
        value: vehicle.id,
        brand,
        name: vehicle.name,
        plate: vehicle.plate,
      }
    })

    if (reservationToEdit.value && !vehicleOptions.value.some(({ value }) => value === reservationToEdit.value.vehicleId)) {
      const brand = getEventBrandName(reservationToEdit.value.vehicleBrandCode)

      vehicleOptions.value.unshift({
        title: `${brand} ${reservationToEdit.value.vehicleName} ${reservationToEdit.value.vehiclePlate}`,
        value: reservationToEdit.value.vehicleId,
        brand,
        name: reservationToEdit.value.vehicleName,
        plate: reservationToEdit.value.vehiclePlate,
      })
    }

    if (!vehicleOptions.value.some(({ value }) => value === reservationFormValues.vehicleId)) {
      reservationFormValues.vehicleId = null
    }
  } catch (error) {
    console.error('Failed to load vehicles for a drive park reservation.', error)
    showRequestErrorToast(error, 'Vozila nije bilo moguće učitati.')
  } finally {
    isLoadingVehicles.value = false
  }
}

async function saveReservationAsync () {
  if (isSavingReservation.value || isLoadingReservation.value || isLoadingVehicles.value) {
    return
  }

  const validationResult = await reservationFormRef.value.validate()

  if (!validationResult.valid) {
    return
  }

  if (reservationFormValues.endDate < reservationFormValues.startDate) {
    showErrorToast('Datum završetka ne može biti prije datuma početka.')
    return
  }

  const company = reservationFormValues.company.trim()
  const fullName = reservationFormValues.fullName.trim()

  if (!company && !fullName) {
    showErrorToast('Barem jedno od polja Tvrtka ili Ime i prezime mora biti ispunjeno.')
    return
  }

  isSavingReservation.value = true

  try {
    const reservationPath = isEditMode.value
      ? `/api/drivepark/reservations/${route.params.reservationId}`
      : '/api/drivepark/reservations'

    await apiClient.postAsync(reservationPath, {
      vehicleId: reservationFormValues.vehicleId,
      startDate: reservationFormValues.startDate,
      endDate: reservationFormValues.endDate,
      company: company || null,
      fullName: fullName || null,
      contact: reservationFormValues.contact.trim() || null,
    })

    showSuccessToast(isEditMode.value
      ? 'Rezervacija je uspješno ažurirana.'
      : 'Rezervacija je uspješno dodana.')
    await router.push(calendarRoute.value)
  } catch (error) {
    console.error(`Failed to ${isEditMode.value ? 'update' : 'create'} a drive park reservation.`, error)

    if (error.status === 409 && ['vehicle_unavailable', 'vehicle_conflict'].includes(error.data?.error)) {
      showErrorToast('Vozilo nije dostupno u odabranom razdoblju.')
    } else {
      showRequestErrorToast(
        error,
        isEditMode.value
          ? 'Rezervaciju nije bilo moguće ažurirati.'
          : 'Rezervaciju nije bilo moguće spremiti.'
      )
    }
  } finally {
    isSavingReservation.value = false
  }
}
</script>

<style scoped lang="scss">
.reservation-form {
  &__field {
    max-width: 560px;
  }

  &__driver-heading {
    margin-top: 24px;
  }

  &__section-heading {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-border);
  }

  &__vehicle-plate {
    margin-left: 8px;
    color: var(--color-text-muted);
  }
}
</style>
